import { Checkbox, Flex, Typography } from "antd";
import { MenuItem } from "types/";
import { useTranslate } from "@refinedev/core";
import { ExperienceLabel } from "../types";
import { ProjectSettingsRequest } from "../../types";
import { useProjectInstance } from "../hooks/useProjectInstance";
import { useAntTheme } from "hooks/useAntTheme/";

type ExperienceType = MenuItem & { key: ExperienceLabel };

export const ExperienceTypeSelector = ({
  experienceOptions,
  projectId,
  instanceId,
  mediaId,
}: {
  experienceOptions: Partial<Record<ExperienceLabel, boolean>>;
  projectId: string;
  instanceId: string;
  mediaId: string;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const { updateSettings, isMutating } = useProjectInstance({
    projectId,
    instanceId,
    mediaId,
  });

  const saveExperienceType = async (key: ExperienceLabel) => {
    const newOptions = {
      ...experienceOptions,
      [key]: !experienceOptions[key],
    };

    const updatedOptions: ProjectSettingsRequest = {
      chat_mode: newOptions.chatBot ? "FullChat" : "Disabled",
      include_document_animations: newOptions.animatedDocument,
    };

    await updateSettings(updatedOptions, true);
  };

  const experienceTypes: ExperienceType[] = [
    {
      id: 1,
      key: ExperienceLabel.Video,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.Video}`
      ),
      hide: true,
    },
    {
      id: 2,
      key: ExperienceLabel.AnimatedDocument,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.AnimatedDocument}`
      ),
    },
    {
      id: 3,
      key: ExperienceLabel.Chatbot,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.Chatbot}`
      ),
    },
    {
      id: 4,
      key: ExperienceLabel.Audio,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.Audio}`
      ),
      hide: true,
    },
    {
      id: 5,
      key: ExperienceLabel.Podcast,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.Podcast}`
      ),
      hide: true,
    },
    {
      id: 6,
      key: ExperienceLabel.Quiz,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.Quiz}`
      ),
      hide: true,
    },
    {
      id: 7,
      key: ExperienceLabel.TextSummary,
      label: t(
        `projects.components.ProjectPublications.${ExperienceLabel.TextSummary}`
      ),
      hide: true,
    },
  ];

  return (
    <Flex gap={10} vertical style={{ marginBottom: 20 }}>
      <Typography.Title
        style={{
          margin: 0,
          fontSize: "24px",
          fontWeight: "500",
          color: theme.colorTextTitle,
        }}
      >
        {t("projects.components.ProjectPublications.experiencePreview.title")}
      </Typography.Title>
      <Flex gap={20} wrap="wrap">
        <Typography.Text type="secondary">
          {t(
            "projects.components.ProjectPublications.experiencePreview.subtitle"
          )}
        </Typography.Text>
        {experienceTypes
          ?.filter((x) => !x.hide)
          ?.map((menuItem) => {
            const label = menuItem.key;
            const isChecked = experienceOptions[label];

            return (
              <Checkbox
                disabled={isMutating}
                checked={isChecked}
                onChange={() => {
                  saveExperienceType(menuItem.key).catch(console.error);
                }}
                key={menuItem.id}
              >
                {menuItem.label}
              </Checkbox>
            );
          })}
      </Flex>
    </Flex>
  );
};
